/**
 * Copyright © IMPACT Commerce AB. All rights reserved.
 * See COPYING.txt for license details.
 */

import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Custom element for the Alpinestars Membership block. Updates the displayed
 * loyalty-points number when the active variant changes, using a server-side
 * rendered `{variant_id: points}` map on `data-variant-points`. Hides itself
 * when the new variant has 0 points.
 */
class AlpinestarsMembershipNew extends Component {
  /** @type {Record<string, number>} */
  #variantPoints = {};
  /** @type {HTMLElement | null} */
  #pointsEl = null;

  connectedCallback() {
    super.connectedCallback();

    try {
      this.#variantPoints = JSON.parse(this.dataset.variantPoints || '{}');
    } catch {
      this.#variantPoints = {};
    }
    this.#pointsEl = this.querySelector('[data-membership-points]');

    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!target) return;
    target.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!target) return;
    target.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  /** @param {CustomEvent} event */
  #onVariantUpdate = (event) => {
    const variant = event.detail?.resource;
    if (!variant?.id) return;

    const points = this.#variantPoints[String(variant.id)] ?? 0;

    if (points > 0) {
      this.hidden = false;
      if (this.#pointsEl) this.#pointsEl.textContent = String(points);
    } else {
      this.hidden = true;
    }
  };
}

if (!customElements.get('alpinestars-membership-new')) {
  customElements.define('alpinestars-membership-new', AlpinestarsMembershipNew);
}
